import { ref, computed } from 'vue'
import type { Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { DocumentItem } from 'shared/src'
import { useAlerts } from '@/composables/alert/useAlerts'
import { alertEnum } from '@/enums/AlertEnum.ts'
import { BULK_DELETE_BATCH_SIZE } from '@/constants/documents.ts'
import {
  useDeleteDocumentMutation,
  useDeleteDocumentsBulkMutation,
} from '@/composables/queries/documents'

export const useDocumentDelete = (selectedKeys: Ref<(string | number)[]>) => {
  const { t } = useI18n()
  const { addAlert } = useAlerts()
  const deleteDocMutation = useDeleteDocumentMutation()
  const bulkDeleteMutation = useDeleteDocumentsBulkMutation()

  const openDeleteModal = ref(false)
  const selectedForDelete = ref<DocumentItem>()
  const isBulkDeleting = ref(false)
  const bulkDeleteRequestedCount = ref(0)

  const toggleDeleteModal = (item: DocumentItem) => {
    selectedForDelete.value = item
    openDeleteModal.value = !openDeleteModal.value
  }

  const openBulkDeleteModal = () => {
    selectedForDelete.value = undefined
    openDeleteModal.value = true
  }

  const onDeleteDocument = async (id: string) => {
    if (!id) return false

    try {
      await deleteDocMutation.mutateAsync(id)
      addAlert(t('document_deleted_successfully'), alertEnum.Success)
      openDeleteModal.value = false
      return true
    } catch {
      return false
    }
  }

  const onBulkDeleteDocuments = async (ids: string[]) => {
    if (ids.length === 0) return false

    try {
      const result = await bulkDeleteMutation.mutateAsync(ids)

      if (result.ok) {
        addAlert(t('documents_deleted_successfully', { count: ids.length }), alertEnum.Success)
        selectedKeys.value = []
        openDeleteModal.value = false
        return true
      }

      const failedIds = result.failedIds
      const allFailed = failedIds.length === ids.length && failedIds.length > 0

      if (!allFailed && failedIds.length > 0) {
        const errorItems = (result.errors ?? []).map((error) => ({ label: error, count: 1 }))
        if (errorItems.length > 0) {
          addAlert(t('some_documents_not_deleted'), alertEnum.Danger, { items: errorItems })
        } else {
          addAlert(t('some_documents_not_deleted'), alertEnum.Danger)
        }
        selectedKeys.value = failedIds
        openDeleteModal.value = false
        return false
      }

      addAlert(t('error_occurred_try_again'), alertEnum.Danger)
      return false
    } catch {
      addAlert(t('error_occurred_try_again'), alertEnum.Danger)
      return false
    }
  }

  const deleteModalItem = computed(() => {
    if (selectedForDelete.value) return selectedForDelete.value.fileName || ''
    if (selectedKeys.value.length === 1) {
      return t('one_selected_document')
    }
    return t('n_selected_documents', [selectedKeys.value.length])
  })

  const onConfirmDelete = async () => {
    if (selectedForDelete.value) {
      return onDeleteDocument(selectedForDelete.value.id)
    }

    const ids = selectedKeys.value.map(String)
    openDeleteModal.value = false
    bulkDeleteRequestedCount.value = ids.length
    isBulkDeleting.value = true

    try {
      return await onBulkDeleteDocuments(ids)
    } finally {
      isBulkDeleting.value = false
      bulkDeleteRequestedCount.value = 0
    }
  }

  const showBulkDeleteLoader = computed(
    () => isBulkDeleting.value && bulkDeleteRequestedCount.value > BULK_DELETE_BATCH_SIZE,
  )

  return {
    openDeleteModal,
    selectedForDelete,
    isBulkDeleting,
    showBulkDeleteLoader,
    deleteModalItem,
    toggleDeleteModal,
    openBulkDeleteModal,
    onDeleteDocument,
    onBulkDeleteDocuments,
    onConfirmDelete,
  }
}
